import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CircleCheck, CircleX, Loader2 } from '@/lib/icons'
import { formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import { invalidateBroadcastCaches, useBroadcastProgress } from './hooks'

export interface ProgressDialogProps {
  /** `null` bo'lsa oyna yopiq — `useBroadcastProgress(null)` so'rov yubormaydi (`enabled: false`). */
  broadcastId: number | null
  onClose: () => void
}

/**
 * Yuborish progressi — `docs/03-app-flow.md`: "Yuborish navbat orqali... Jarayon progress
 * bar bilan ko'rsatiladi". `hooks.ts`dagi `useBroadcastProgress()` `GET /api/broadcasts/:id`ni
 * `status`ga qarab avtomatik pollaydi va `'done'`/`'failed'`da to'xtaydi.
 *
 * Diqqat: oyna yopilsa ham yuborish TO'XTAMAYDI — server fon rejimida davom ettiradi
 * (`services/broadcast.ts`). Bu oyna faqat KUZATISH uchun, "Yopish" bosilishi shunchaki
 * pollingni to'xtatadi (`broadcastId` `null` bo'lganda `enabled: false`), broadcastning o'zi
 * emas.
 */
export function ProgressDialog({ broadcastId, onClose }: ProgressDialogProps) {
  const queryClient = useQueryClient()
  const { broadcast, error } = useBroadcastProgress(broadcastId)
  const invalidatedForId = useRef<number | null>(null)

  // Yuborish tugagach (`done`/`failed`) "Tarix" ro'yxati eskirgan bo'ladi — sonlar
  // (`sentCount`/`failedCount`) endi FINAL, lekin ro'yxat keshi hali eski qiymatni
  // ko'rsatadi. Bitta `id` uchun BIR MARTA invalidatsiya qilinadi (`invalidatedForId`
  // orqali) — aks holda har poll tsiklida keraksiz qayta so'rov ketaverardi.
  useEffect(() => {
    if (!broadcast) return
    if (broadcast.status !== 'done' && broadcast.status !== 'failed') return
    if (invalidatedForId.current === broadcast.id) return
    invalidatedForId.current = broadcast.id
    invalidateBroadcastCaches(queryClient)
  }, [broadcast, queryClient])

  const open = broadcastId !== null
  const isActive = broadcast?.status === 'pending' || broadcast?.status === 'sending'
  const isDone = broadcast?.status === 'done'
  const isFailed = broadcast?.status === 'failed'

  const sent = broadcast ? broadcast.sentCount : 0
  const total = broadcast ? broadcast.totalCount : 0
  const progressPercent = total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0

  return (
    <Modal open={open} onClose={onClose} ariaLabel="Yuborish jarayoni" size="compact" zIndex={50}>
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">
          {isActive && 'Yuborilmoqda...'}
          {isDone && 'Yuborish yakunlandi'}
          {isFailed && 'Yuborishda xatolik'}
          {!broadcast && !error && 'Yuklanmoqda...'}
        </h2>

        {error && <p className="text-sm font-medium text-urgency-overdue-text">{error.message}</p>}

        {broadcast && (
          <>
            <div className="flex flex-col gap-1.5">
              {/* Skrinrider progress qiymatini ko'ra olishi uchun — vizual kenglik o'zgarishi
                  o'zi hech qanday a11y ma'lumot bermaydi (`role`/`aria-value*` yo'q edi). */}
              <div
                role="progressbar"
                aria-label="Yuborish jarayoni"
                aria-valuenow={progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-2 w-full overflow-hidden rounded-full bg-app-border/60"
              >
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-300 ease-out',
                    isFailed ? 'bg-urgency-overdue' : 'bg-brand',
                  )}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="tabular text-sm text-app-hint">
                {formatNumber(sent)} / {formatNumber(total)} yuborildi
                {broadcast.failedCount > 0 && (
                  <span className="text-urgency-overdue-text">
                    {' '}
                    · {formatNumber(broadcast.failedCount)} yetmadi
                  </span>
                )}
              </p>
            </div>

            {isActive && (
              <p className="inline-flex items-center gap-2 text-sm text-app-hint">
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                Xabarlar navbat bilan yuborilmoqda, bu bir necha daqiqa davom etishi mumkin.
              </p>
            )}

            {isDone && (
              <p className="inline-flex items-center gap-2 text-sm text-urgency-done-text">
                <CircleCheck size={16} aria-hidden="true" />
                <span className="tabular font-medium">
                  {formatNumber(broadcast.sentCount)} ta
                </span>{' '}
                blogerga yetkazildi
                {broadcast.failedCount > 0 && (
                  <>
                    ,{' '}
                    <span className="tabular font-medium text-urgency-overdue-text">
                      {formatNumber(broadcast.failedCount)} ta
                    </span>{' '}
                    yetmadi
                  </>
                )}
                .
              </p>
            )}

            {isFailed && (
              <p className="inline-flex items-center gap-2 text-sm text-urgency-overdue-text">
                <CircleX size={16} aria-hidden="true" />
                Yuborishda kutilmagan xatolik yuz berdi. Tarix bo&apos;limida qayta ko&apos;ring
                yoki yangi broadcast yarating.
              </p>
            )}
          </>
        )}

        <div className="flex justify-end pt-1">
          <Button variant={isActive ? 'secondary' : 'primary'} onClick={onClose}>
            {isActive ? 'Fonda davom etadi' : 'Yopish'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
